/**
 * Create Contract Form
 *
 * Standalone form for creating contracts with optional customer selection
 */

import type { CreateContract } from '@monorepo/shared/schemas/contracts'
import { useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { usePlans } from '@/features/plans/hooks/usePlans'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import { useCreateContract } from '../hooks/useContracts'

interface CreateContractFormProps {
  customerId?: string
}

export function CreateContractForm({ customerId }: CreateContractFormProps) {
  const navigate = useNavigate()
  const { mutate: createContract, isPending } = useCreateContract()

  // Load available plans using React Query
  const { data: plansResult, isLoading: isLoadingPlans } = usePlans({
    category: 'plan',
    status: 'active',
  })
  const availablePlans = plansResult?.data || []

  const [formData, setFormData] = useState<Partial<CreateContract>>({
    customer_id: customerId || '',
    plan_id: '',
    name: '',
    description: '',
    start_date: new Date().toISOString().split('T')[0],
    billing_interval: 'monthly',
    currency: 'USD',
    collection_method: 'charge_automatically',
    payment_terms: 30,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.customer_id || !formData.plan_id || !formData.name) {
      return
    }

    createContract(formData as CreateContract, {
      onSuccess: data => {
        navigate({ to: `/contracts/${data.id}` })
      },
    })
  }

  const handleInputChange = (
    field: keyof CreateContract,
    value: string | number
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>Contract Information</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Customer Selector (if not provided) */}
          {!customerId && (
            <div className='space-y-2'>
              <Label htmlFor='customer_id'>
                Customer <span className='text-red-500'>*</span>
              </Label>
              <Input
                id='customer_id'
                placeholder='Customer ID'
                value={formData.customer_id || ''}
                onChange={e => handleInputChange('customer_id', e.target.value)}
                required
              />
              <p className='text-xs text-muted-foreground'>
                Enter the customer ID for this contract
              </p>
            </div>
          )}

          {/* Plan Selector */}
          <div className='space-y-2'>
            <Label htmlFor='plan_id'>
              Plan <span className='text-red-500'>*</span>
            </Label>
            <Select
              value={formData.plan_id || ''}
              onValueChange={value => handleInputChange('plan_id', value)}
              disabled={isLoadingPlans}
            >
              <SelectTrigger id='plan_id'>
                <SelectValue placeholder='Select a plan' />
              </SelectTrigger>
              <SelectContent>
                {isLoadingPlans ? (
                  <SelectItem value='' disabled>
                    Loading plans...
                  </SelectItem>
                ) : availablePlans.length === 0 ? (
                  <SelectItem value='' disabled>
                    No active plans available
                  </SelectItem>
                ) : (
                  availablePlans.map(plan => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name}
                      {plan.description && (
                        <span className='text-muted-foreground text-xs'>
                          {' '}
                          - {plan.description}
                        </span>
                      )}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <p className='text-xs text-muted-foreground'>
              Select the plan for this contract
            </p>
          </div>

          {/* Contract Name */}
          <div className='space-y-2'>
            <Label htmlFor='name'>
              Contract Name <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='name'
              placeholder='e.g., Enterprise Annual Contract'
              value={formData.name || ''}
              onChange={e => handleInputChange('name', e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div className='space-y-2'>
            <Label htmlFor='description'>Description</Label>
            <Textarea
              id='description'
              placeholder='Optional contract description'
              value={formData.description || ''}
              onChange={e => handleInputChange('description', e.target.value)}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Billing Configuration</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          {/* Start Date */}
          <div className='space-y-2'>
            <Label htmlFor='start_date'>
              Start Date <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='start_date'
              type='date'
              value={formData.start_date || ''}
              onChange={e => handleInputChange('start_date', e.target.value)}
              required
            />
          </div>

          {/* End Date */}
          <div className='space-y-2'>
            <Label htmlFor='end_date'>End Date</Label>
            <Input
              id='end_date'
              type='date'
              value={formData.end_date || ''}
              onChange={e => handleInputChange('end_date', e.target.value)}
            />
            <p className='text-xs text-muted-foreground'>
              Leave empty for ongoing contract
            </p>
          </div>

          {/* Billing Interval */}
          <div className='space-y-2'>
            <Label htmlFor='billing_interval'>
              Billing Interval <span className='text-red-500'>*</span>
            </Label>
            <Select
              value={formData.billing_interval}
              onValueChange={value =>
                handleInputChange('billing_interval', value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='daily'>Daily</SelectItem>
                <SelectItem value='weekly'>Weekly</SelectItem>
                <SelectItem value='monthly'>Monthly</SelectItem>
                <SelectItem value='quarterly'>Quarterly</SelectItem>
                <SelectItem value='semi_annual'>Semi-Annual</SelectItem>
                <SelectItem value='annual'>Annual</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Currency */}
          <div className='space-y-2'>
            <Label htmlFor='currency'>
              Currency <span className='text-red-500'>*</span>
            </Label>
            <Select
              value={formData.currency}
              onValueChange={value => handleInputChange('currency', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='USD'>USD</SelectItem>
                <SelectItem value='EUR'>EUR</SelectItem>
                <SelectItem value='GBP'>GBP</SelectItem>
                <SelectItem value='JPY'>JPY</SelectItem>
                <SelectItem value='AUD'>AUD</SelectItem>
                <SelectItem value='CAD'>CAD</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Collection Method */}
          <div className='space-y-2'>
            <Label htmlFor='collection_method'>Collection Method</Label>
            <Select
              value={formData.collection_method}
              onValueChange={value =>
                handleInputChange('collection_method', value)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='charge_automatically'>
                  Charge Automatically
                </SelectItem>
                <SelectItem value='send_invoice'>Send Invoice</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Payment Terms */}
          <div className='space-y-2'>
            <Label htmlFor='payment_terms'>Payment Terms (days)</Label>
            <Input
              id='payment_terms'
              type='number'
              min={0}
              value={formData.payment_terms || 30}
              onChange={e =>
                handleInputChange('payment_terms', parseInt(e.target.value, 10))
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className='flex justify-end gap-3'>
        <Button
          type='button'
          variant='outline'
          onClick={() => navigate({ to: '/contracts' })}
        >
          Cancel
        </Button>
        <Button type='submit' disabled={isPending}>
          {isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          {isPending ? 'Creating...' : 'Create Contract'}
        </Button>
      </div>
    </form>
  )
}
